import { faArrowUpRight, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { DisplayDateFromNow } from "src/base-components/Date";
import { Cell, Header, TableComp as Table } from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseStatusPillWithSchema } from "src/cases/common/CaseStatusPillWithSchema";
import {
  DeserializedCase,
  PropertyCaseGroup,
} from "src/cases/createCase/useOpenCasesForMappedEntities";
import { Icon, IconButton } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { getBaseUrl, getUrlToCaseViewPage } from "src/router/urls";

const columnHelper = createColumnHelper<DeserializedCase>();

type OpenCasesModalProps = {
  isOpen: boolean;
  entityDisplayName: string;
  groups: PropertyCaseGroup[];
  wsId: string;
  orgId: string;
  onClose: () => void;
  afterLeave: () => void;
};

export const OpenCasesModal: React.FC<OpenCasesModalProps> = ({
  isOpen,
  entityDisplayName,
  groups,
  wsId,
  orgId,
  onClose,
  afterLeave,
}) => {
  const openCaseInNewTab = (caseId: string) => {
    const fullUrl = getBaseUrl() + getUrlToCaseViewPage(orgId, wsId, caseId);
    window.open(fullUrl, "_blank");
  };

  const columns = [
    columnHelper.accessor("id", {
      header: (info) => (
        <Header dataLoc="open-cases-header-id" info={info}>
          <span className="whitespace-nowrap">Case ID</span>
        </Header>
      ),
      size: 100,
      cell: (info) => (
        <Cell className="w-24" info={info}>
          <TruncatedIdPill
            feedback="inline-only-checkmark"
            id={info.getValue()}
          />
        </Cell>
      ),
    }),
    columnHelper.accessor("case_properties.status", {
      header: (info) => (
        <Header dataLoc="open-cases-header-status" info={info}>
          <div className="flex items-center gap-x-0.5">
            <Icon icon={faSpinner} size="2xs" />
            Status
          </div>
        </Header>
      ),
      size: 150,
      cell: (info) => {
        const caseData = info.row.original;
        return (
          <Cell dataLoc={`open-cases-cell-status-${info.row.id}`} info={info}>
            <CaseStatusPillWithSchema
              status={caseData.case_properties.status}
              subStatus={caseData.case_properties.sub_status}
              templateId={caseData.template.id}
              versionId={caseData.template_version.id}
            />
          </Cell>
        );
      },
    }),
    columnHelper.accessor("created_at", {
      header: (info) => (
        <Header dataLoc="open-cases-header-created-at" info={info}>
          Created at
        </Header>
      ),
      size: 150,
      cell: (info) => (
        <Cell info={info}>
          <DisplayDateFromNow value={info.getValue()} />
        </Cell>
      ),
    }),
    columnHelper.display({
      id: "actions",
      size: 50,
      cell: ({ row }) => (
        <IconButton
          icon={faArrowUpRight}
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            openCaseInNewTab(row.original.id);
          }}
        />
      ),
    }),
  ];

  return (
    <Modal afterLeave={afterLeave} open={isOpen} onClose={onClose}>
      <Modal.Header description="Review existing Cases before creating a new one to avoid duplicate investigations.">
        {`Open Cases for ${entityDisplayName}`}
      </Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <div key={group.propertyKey}>
              <p className="mb-1 font-inter-medium-12px text-gray-500">
                {group.propertyDisplayName}
              </p>
              <Table
                columns={columns}
                data={group.cases}
                dataLoc={`open-cases-table-${group.propertyKey}`}
                variant="compact"
              />
            </div>
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={null}
        secondaryButton="Back to Case creation"
      />
    </Modal>
  );
};
